"use client";

export type TopicItemKind = "tag" | "tool";

export interface TopicItem {
  id: string;
  label: string;
  kind: TopicItemKind;
  count?: number;
}

function resolveMergedCount({
  currentCount,
  nextCount,
}: {
  currentCount?: number;
  nextCount?: number;
}): number | undefined {
  const maxCount = Math.max(currentCount ?? 0, nextCount ?? 0);
  return maxCount > 0 ? maxCount : undefined;
}

export function sortTopicItems(a: TopicItem, b: TopicItem): number {
  return (b.count ?? 0) - (a.count ?? 0) || a.label.localeCompare(b.label);
}

export function mergeTopicItems({
  items,
}: {
  items: TopicItem[];
}): TopicItem[] {
  const mergedById = new Map<string, TopicItem>();

  for (const item of items) {
    const current = mergedById.get(item.id);
    if (!current) {
      mergedById.set(item.id, item);
      continue;
    }

    const preferredItem =
      current.kind === "tool" || item.kind !== "tool" ? current : item;

    mergedById.set(item.id, {
      ...preferredItem,
      count: resolveMergedCount({
        currentCount: current.count,
        nextCount: item.count,
      }),
    });
  }

  return [...mergedById.values()].sort(sortTopicItems);
}

export function filterTopicItems<TTopicItem extends TopicItem>({
  items,
  query,
}: {
  items: TTopicItem[];
  query: string;
}): TTopicItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) {
    return items;
  }

  return items.filter((item) =>
    item.label.toLowerCase().includes(normalizedQuery)
  );
}
